import React, { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Plans() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const autoTriggered = useRef(false);

  const load = () => api.get("/plans").then(({ data }) => setData(data)).catch(() => setData({ plans: [] }));
  useEffect(() => { load(); }, []);

  const subscribe = async (lk) => {
    if (lk === "sj_enterprise") { toast.info("Contact sales@sjpropertycare.com for Enterprise pricing."); return; }
    setBusy(lk);
    try {
      const { data } = await api.post("/subscriptions/checkout", { lookup_key: lk, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error("Could not start checkout"); setBusy(""); }
  };

  // Auto-start checkout for a plan chosen on the public site (via ?plan= or stored intent).
  useEffect(() => {
    if (!data || autoTriggered.current) return;
    const pending = new URLSearchParams(window.location.search).get("plan") || localStorage.getItem("sj_pending_plan");
    if (!pending) return;
    autoTriggered.current = true;
    localStorage.removeItem("sj_pending_plan");
    if (pending !== "sj_enterprise" && data.plans?.some((p) => p.lookup_key === pending) && data.current?.lookup_key !== pending) {
      subscribe(pending);
    }
  }, [data]);

  const cancel = async () => { try { await api.post("/subscriptions/cancel"); toast.success("Subscription cancelled"); load(); } catch (e) { toast.error("Failed"); } };
  const manageBilling = async () => {
    try {
      const { data } = await api.post("/subscriptions/manage-billing", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open billing portal"); }
  };

  return (
    <DashboardLayout nav={getNav("client")} title="Subscription Plans">
      {!data ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="space-y-6">
          {data.current && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-emerald-800">You're subscribed to the <strong>{(data.plans.find((p) => p.lookup_key === data.current.lookup_key) || {}).name || data.current.lookup_key}</strong> plan.{data.current.status && data.current.status !== "active" ? ` (${data.current.status})` : ""}</div>
              <div className="flex items-center gap-2">
                <Button data-testid="manage-billing-btn" variant="outline" size="sm" className="border-emerald-300 text-emerald-700" onClick={manageBilling}>Manage Billing</Button>
                <Button data-testid="cancel-sub-btn" variant="outline" size="sm" className="border-emerald-300 text-emerald-700" onClick={cancel}>Cancel Subscription</Button>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.plans.map((p, i) => {
              const isCurrent = data.current?.lookup_key === p.lookup_key;
              const featured = p.lookup_key === "sj_portfolio_monthly";
              return (
                <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`} className={`rounded-2xl border p-6 flex flex-col ${featured ? "border-blue-500 ring-2 ring-blue-500/20 bg-white" : "border-slate-200 bg-white"}`}>
                  {featured && <span className="self-start mb-2 text-[10px] font-bold uppercase tracking-wider text-blue-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Most Popular</span>}
                  {p.lookup_key === "sj_enterprise" && <Crown className="w-5 h-5 text-amber-500 mb-2" />}
                  <h3 className="font-head text-xl font-semibold text-slate-900">{p.name}</h3>
                  <div className="mt-2 font-head text-3xl font-bold text-slate-900">{p.amount ? <>{money(p.amount)}<span className="text-sm font-normal text-slate-400">/mo</span></> : "Custom"}</div>
                  <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.features.map((f, j) => <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {f}</li>)}
                  </ul>
                  <Button data-testid={`subscribe-${p.lookup_key}`} disabled={isCurrent || busy === p.lookup_key} onClick={() => subscribe(p.lookup_key)}
                    className={`mt-5 w-full ${featured ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}>
                    {busy === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Current Plan" : p.lookup_key === "sj_enterprise" ? "Contact Sales" : "Subscribe"}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 max-w-3xl">{data.disclaimer}</p>
        </div>
      )}
    </DashboardLayout>
  );
}
